































import { bemBuilder } from "@/v2/util/bem-builder";
import {
  computed,
  defineComponent,
  getCurrentInstance,
  PropType,
  ref,
  watch,
} from "@vue/composition-api";
import { AtomInput } from "@/v2/new-design-system/atom/input";
import { MolFormControl } from "@/v2/new-design-system/mol/form-control";
import { AtomText, AtomTextColorEnum } from "../../atom/text";
import { CurrencyCodeEnum } from "@/v2/enum";
import { toLocaleCurrency } from "@/v2/util/to-locale-currency";

const css = bemBuilder("org-form-money-input");

export default defineComponent({
  name: "OrgFormMoneyInput",
  components: {
    MolFormControl,
    AtomInput,
    AtomText,
  },
  props: {
    value: {
      type: Number,
      required: true,
    },
    currencyCode: {
      type: String as PropType<CurrencyCodeEnum>,
      required: true,
    },
    locale: {
      type: String,
      default: "en-US",
    },
    helpText: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    errorMsg: {
      type: String,
      default: "",
    },
    successMsg: {
      type: String,
      default: "",
    },
    onChange: {
      type: Function as PropType<(cents: number) => void>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const displayAmount = ref("0");
    const rawAmount = ref(0);
    const symbol = computed(() => {
      try {
        const currency = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: props.currencyCode,
        })
          .formatToParts(1)
          .find((obj) => obj.type === "currency");

        return currency?.value ?? props.currencyCode;
      } catch (e) {
        return props.currencyCode;
      }
    });

    function toMoney(valueInCents: number) {
      return toLocaleCurrency(
        valueInCents,
        props.currencyCode,
        "0,0",
        props.locale
      );
    }

    const toInteger = (value: string | number) => {
      if ((typeof value === "number" && isNaN(value)) || value === "") {
        value = 0;
      }

      let clearedString = new String(value)
        .toString()
        .replaceAll(/[^0-9]/g, "")
        .replaceAll(new RegExp(/\.|,/g), "");

      if (clearedString === "") {
        clearedString = "0";
      }

      return parseInt(clearedString, 10);
    };

    const vm = getCurrentInstance();

    const previousDisplayLength = ref();
    const cursorPositionStorage = ref();
    const latestCursorPosition = ref(0);
    const ensureCursorPosition = () => {
      // buffer to add extra jump on new (.) or (,)
      const buffer =
        displayAmount.value.length - previousDisplayLength.value > 1 ? 1 : 0;

      latestCursorPosition.value = cursorPositionStorage.value + buffer;
    };

    watch(
      () => props.value,
      (newValue) => {
        if (cursorPositionStorage.value) {
          vm?.proxy.$nextTick(() => {
            ensureCursorPosition();
          });
        }

        if (newValue === rawAmount.value) return;

        displayAmount.value = toMoney(newValue);
      },
      {
        immediate: true,
      }
    );

    function setValue(newInput: string, cursorPosition: number) {
      cursorPositionStorage.value = cursorPosition;
      previousDisplayLength.value = displayAmount.value.length;
      rawAmount.value = toInteger(newInput);
      displayAmount.value = toMoney(rawAmount.value);
      vm?.proxy.$forceUpdate();
      props.onChange(rawAmount.value);
    }

    return {
      css,
      symbol,
      setValue,
      latestCursorPosition,
      displayAmount,
      AtomTextColorEnum,
    };
  },
});
